import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
// Removed redundant hook imports - now using props
import {isMobile} from "react-device-detect";
import ProgressBar from "@ramonak/react-progress-bar";
import {ReactElement, useEffect} from "react";
import {IoChevronForward} from "react-icons/io5";
import {FiChevronRight, FiGift} from "react-icons/fi";
import useWindowSize from "@/hooks/useWindowSize";
import styles from "./UserProfile.module.scss";

interface UserProfileProps {
    userProfile?: any; // GetUserResponse type
    accountLevel?: any; // AccountLevel type
    onLevelsClick?: () => void;
    onBadgeClick?: () => void;
    onAddFuelClick?: () => void;
    forceMobileView?: boolean; // Force mobile view regardless of window size
    dataCard?: boolean; // Render as compact data card (for inline use on games pages, etc.)
    fuelBalance?: string; // Fuel balance to display
    cockpitView?: boolean; // Special layout for cockpit me-column
}

export default function UserProfile({ userProfile, accountLevel, onLevelsClick, onBadgeClick, onAddFuelClick, forceMobileView, dataCard, fuelBalance, cockpitView }: UserProfileProps) {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    /*
     * Remove redundant API calls - use props instead
     * const {data: userProfile} = useGetUserProfile(fullToken);  // REMOVED - using prop
     * const {data: userLevelData} = useGetUserLevel(fullToken, currentUser?.user?.id);  // REMOVED - using prop
     */
    const [windowWidth] = useWindowSize();
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);

    /*
     * Use forceMobileView prop if provided, otherwise check window width
     * Default to desktop view if windowWidth is not yet available
     */
    const isMobileView = forceMobileView === true ? true : windowWidth > 0 ? windowWidth < 768 : false;

    // Always call the hook (React rules) but use prop if available
    const reduxAccountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);
    const accountLevelData = accountLevel || reduxAccountLevel;
    const userLevel = accountLevelData?.level ?? 1;
    const experienceGained = fullToken && accountLevelData ? accountLevelData.experienceGainedAtThisLevel : 0;
    const totalLevelExperience = fullToken && accountLevelData ? accountLevelData.experienceGainedAtThisLevel + accountLevelData.experienceRequiredForNextLevel : 0;

    // No longer need to update Redux here - parent component handles it

    // Special cockpit layout
    if (cockpitView) {
        return (
            <div className={`${styles.profile} ${styles.cockpitVariant}`}>
                {/* Row 1: Avatar | Level Badge | Gift Icon */}
                <div className={styles.profileHeaderRow}>
                    <div className={styles.profilePic}>
                        {fullToken && userProfile?.user?.gravatar
                            ? <img
                                src={userProfile.user.gravatar}
                                alt="User Profile"
                            />
                            : <img
                                src={`${import.meta.env.VITE_CDN_URL}/images/devourgo-profile.webp`}
                                alt="User Profile"
                            />
                        }
                    </div>

                    <button
                        className={styles.levelBadge}
                        onClick={onBadgeClick}
                        title="View Leaderboard"
                        style={{
                            background: "rgba(255, 66, 56, 0.1)",
                            border: "1px solid #FF4238",
                            borderRadius: "20px",
                            padding: "0.25rem 0.75rem",
                            color: "rgba(255, 255, 255, 0.7)",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            cursor: "pointer",
                            boxShadow: "0 0 0 0.5px rgba(255, 66, 56, 0.3)",
                            transition: "all 0.2s ease",
                        }}
                    >
                        Level {userLevel}
                    </button>

                    <button
                        className={styles.giftIcon}
                        onClick={onLevelsClick}
                        title="Level Rewards"
                        style={{
                            background: "rgba(255, 66, 56, 0.1)",
                            border: "1px solid #FF4238",
                            borderRadius: "20px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "rgba(255, 255, 255, 0.7)",
                            fontSize: "1.125rem",
                            boxShadow: "0 0 0 0.5px rgba(255, 66, 56, 0.3)",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <FiGift />
                    </button>
                </div>

                {/* Row 2: XP Data with Progress Bar */}
                <div className={styles.xpSection}>
                    <div className={styles.xpDisplay}>
                        <span className={styles.xpValue}>
                            {accountLevelData?.experienceGainedAtThisLevel?.toLocaleString() || 0}
                        </span>
                        <span className={styles.xpLabel}>XP</span>
                        <span className={styles.xpTotal}>
                            / {totalLevelExperience?.toLocaleString() || 0}
                        </span>
                    </div>

                    <div className={styles.progressContainer}>
                        <div
                            className={styles.progressBar}
                            style={{
                                width: `${totalLevelExperience > 0 ? experienceGained / totalLevelExperience * 100 : 0}%`,
                                background: "linear-gradient(90deg, #7859e6 0%, #FF4238 100%)",
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.profile} ${dataCard ? styles.gamesVariant : styles.playVariant} ${isMobileView && !dataCard ? styles.mobileVariant : ""}`}>
            <div className={styles.profilePic}>
                {fullToken
                    ? <>
                        {userProfile?.user?.gravatar &&
                            <img
                                src={userProfile.user.gravatar}
                                alt="User Profile"
                            />
                        }
                    </>
                    : <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/devourgo-profile.webp`}
                        alt="User Profile"
                    />
                }
            </div>

            <div className={styles.content}>
                <button
                    className={`${styles.levelBadge} ${styles.levelBadgeClickable}`}
                    onClick={onBadgeClick}
                    title="View Leaderboard"
                >
                    {isMobileView && !dataCard ? "" : "Level "}{userLevel}
                </button>
                <div className={styles.xpContainer}>
                    <div className={styles.xpLeft}>
                        <span className={styles.xpCounter}>
                            {fullToken ? accountLevelData?.experienceGainedAtThisLevel : 0}
                        </span>
                        <span className={styles.xpPoints}>
                            XP
                        </span>
                        {/* Show XP total inline on mobile */}
                        {(isMobileView || dataCard) && fullToken &&
                            <span className={styles.xpTotalInline}>
                                / {totalLevelExperience.toLocaleString()}
                            </span>
                        }
                    </div>
                    <div>
                        <button
                            className={`${styles.rewardsButton} reset-button`}
                            onClick={onLevelsClick}
                            title="Level Rewards"
                        >
                            {isMobileView || dataCard
                                ? <FiGift />
                                : <>
                                    <strong>Level Rewards</strong>
                                    <FiChevronRight/>
                                </>
                            }
                        </button>
                        {/* Only show XP total here on desktop */}
                        {!isMobileView && !dataCard &&
                            <div className={styles.xpTotal}>
                                {experienceGained.toLocaleString()} / {totalLevelExperience.toLocaleString()}
                            </div>
                        }
                    </div>
                </div>

                <div className={styles.progressBar}>
                    <ProgressBar
                        height="15px"
                        bgColor={"linear-gradient(90deg, #7859e6 0%, #FF4238 100%)"}
                        baseBgColor={"#212123"}
                        className={styles.progressBarFill}
                        isLabelVisible={false}
                        completed={fullToken && accountLevelData
                            ? accountLevelData.experienceGainedAtThisLevel
                            : 0}
                        maxCompleted={fullToken && accountLevelData
                            ? accountLevelData.experienceGainedAtThisLevel +
                            accountLevelData.experienceRequiredForNextLevel
                            : 0}
                    />
                </div>
            </div>
        </div>
    );
}